import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";

interface Props {
  /** Pre-formatted "Page X of Y" label shown on the left (i18n by the caller). */
  label: string;
  /** Whether a previous page exists (drives the Prev button disabled state). */
  hasPrevious: boolean;
  /** Whether a next page exists (drives the Next button disabled state). */
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  /**
   * Visual variant:
   * - "bare" (default): a plain flex row with raw buttons — the look used by
   *   the ~19 admin/integration consumers. Kept byte-identical so those pages
   *   are visually untouched.
   * - "card": the unified dispatcher look (Team, Vehicles, Warehouses) — a
   *   rounded card wrapper with shared secondary buttons.
   */
  variant?: "bare" | "card";
}

/**
 * Shared prev/next pagination control.
 *
 * The default "bare" variant is used across the admin pages
 * (AdminTenantsPage, AdminTenantDetailPage, AdminBillingPage,
 * AdminActivityLogPage, integration sections, etc.). Its markup and classes are
 * identical to the previous per-page implementations so the rendered output is
 * unchanged.
 *
 * The opt-in "card" variant provides the unified dispatcher look shared by the
 * Team, Vehicles and Warehouses list pages.
 *
 * The "Page X of Y" wording differs per page, so the formatted `label` is
 * passed in by the caller; only the Prev/Next button labels are localized here.
 */
export function Pagination({
  label,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  variant = "bare",
}: Props) {
  const { t } = useTranslation();

  if (variant === "card") {
    return (
      <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-white px-5 py-3 shadow-sm">
        <span className="text-xs text-gray-400">{label}</span>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            aria-label={t("common.previous")}
            onClick={onPrevious}
            disabled={!hasPrevious}
          >
            {t("common.previous")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            aria-label={t("common.next")}
            onClick={onNext}
            disabled={!hasNext}
          >
            {t("common.next")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-gray-500">{label}</p>
      <div className="flex gap-2">
        <button
          type="button"
          aria-label={t("common.previous")}
          onClick={onPrevious}
          disabled={!hasPrevious}
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {t("common.previous")}
        </button>
        <button
          type="button"
          aria-label={t("common.next")}
          onClick={onNext}
          disabled={!hasNext}
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {t("common.next")}
        </button>
      </div>
    </div>
  );
}
